import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import LocalizationContext from '../../contexts/Localizations';
import { ImageBasic, ImageWithFocalPoint, Link } from '../common';
import Watch from '../Watch';
import { PlaylistItem } from '../Watch/type';
import HomepageHeader from '../HomepageHeader';
import { EventHeaderProps, Events } from './type';
import LiveEventsCarousel from './components/LiveEventsCarousel';
import HubEventsCarousel from './components/HubEventsCarousel';
import './styles.scss';

const EventHeader = ({
  eventTitle,
  patternImageTopLeft,
  patternImageTopRight,
  patternImageBottomLeft,
  patternImageBottomRight,
  eventLogoHomepage,
  eventLogoEventHub,
  eventDates,
  eventStartDate,
  eventLocation,
  eventTime,
  linkOne,
  linkTwo,
  homepageCarousel,
  eventHubImage,
  isHomepage,
  raceItems,
  eventHasFinished,
}: EventHeaderProps) => {
  const timezone = eventTime === 'BST' || eventTime === 'GMT' ? 'Europe/London' : eventTime;

  const { translations, locale } = useContext(LocalizationContext);

  const [liveStreamPlaylist, setLiveStreamPlaylist] = useState<PlaylistItem[] | undefined>();
  const [time, setTime] = useState(moment().tz(timezone || 'CET').format('HH:mm'));

  const currentMoment = moment();
  const eventDayCount = moment(eventStartDate) <= currentMoment
    ? currentMoment.diff(moment(eventStartDate), 'days') + 1
    : currentMoment.diff(moment(eventStartDate), 'days') - 1;

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(moment().tz(timezone || 'CET').format('HH:mm'));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const sortedRaces = raceItems?.filter((item) => item.racePicker)
    .sort((a, b) => a.raceStartTime.localeCompare(b.raceStartTime)
      || a.racePicker?.nameEng?.localeCompare(b?.racePicker?.nameEng));

  const organisedEvents = sortedRaces?.reduce((acc, event) => {
    const startDate = moment(event.raceStartTime);
    const endDate = moment(event.raceEndTime);

    const isPast = endDate.isBefore(currentMoment);
    const isBetween = currentMoment.isBetween(startDate, endDate);
    const isUpcoming = currentMoment.isBefore(startDate);

    if (isPast) {
      acc.pastEvents.push(event);
    } else if (isBetween) {
      acc.liveEvents.push(event);
    } else if (isUpcoming) {
      acc.upcomingEvents.push(event);
    }
    return acc;
  }, {
    pastEvents: [],
    liveEvents: [],
    upcomingEvents: [],
  } as Events);

  useEffect(() => {
    if (sortedRaces) {
      const eventsWithLivestream = sortedRaces.filter((item) => item.liveStreamEndTime
        && item.liveStreamStartTime && item.videoPicker
        && moment(item.liveStreamEndTime).isAfter(currentMoment))
        .sort((a, b) => a.liveStreamStartTime?.localeCompare(b.liveStreamStartTime));

      const playlist = eventsWithLivestream?.map((event) => {
        const {
          liveStreamEndTime, liveStreamStartTime, videoPicker, racePicker, discipline,
        } = event;

        const startTime = moment(liveStreamStartTime);
        const endTime = moment(liveStreamEndTime);
        const isLive = currentMoment.isBetween(startTime, endTime);

        return {
          ...videoPicker,
          description: locale === 'fr-fr' ? racePicker?.nameFra : racePicker?.nameEng,
          title: discipline,
          isLive,
          startTime: liveStreamStartTime.slice(11, 16),
          endTime: liveStreamEndTime.slice(11, 16),
        };
      }) as PlaylistItem[];

      setLiveStreamPlaylist(playlist);
    }
  }, []);

  const liveOrUpcomingEvents = [
    ...organisedEvents?.liveEvents || [], ...organisedEvents?.upcomingEvents || [],
  ].length;

  const isSingleDigit = eventDayCount > 0 && eventDayCount < 10;

  const isOnlyOneLink = (linkOne || linkTwo) && !(linkOne && linkTwo);
  const displayLivePlaylist = !isHomepage && !!liveOrUpcomingEvents && liveStreamPlaylist?.length;
  const withLiveEvents = Boolean(organisedEvents?.liveEvents.length);

  return (
    <div className={`event-header ${isHomepage ? 'event-header--homepage' : 'event-header--event-hub'}`}>
      {patternImageTopLeft && (
        <span className="event-header__pattern--top-left">
          <ImageBasic image={patternImageTopLeft} />
        </span>
      )}

      {patternImageTopRight && (
        <span className="event-header__pattern--top-right">
          <ImageBasic image={patternImageTopRight} />
        </span>
      )}

      {patternImageBottomLeft && (
        <span className="event-header__pattern--bottom-left">
          <ImageBasic image={patternImageBottomLeft} />
        </span>
      )}

      {patternImageBottomRight && (
        <span className="event-header__pattern--bottom-right">
          <ImageBasic image={patternImageBottomRight} />
        </span>
      )}

      <div className={`event-header__container ${displayLivePlaylist ? 'with-playlist' : ''}`}>
        {(eventHubImage && !isHomepage) && (
          <div className="event-header__background-image">
            <ImageWithFocalPoint
              image={eventHubImage}
              aspectRatio="2x1"
              aspectRatioMobile="2x3"
              widthSizes={['400', '1000', '1600']}
            />
          </div>
        )}

        <div className={`event-header__logo ${isHomepage ? 'logo-homepage' : 'logo-hub'}`}>
          {isHomepage
            ? eventLogoHomepage && (
              <ImageBasic image={eventLogoHomepage} useIntrinsicHeight />
            )
            : eventLogoEventHub && (
              <ImageBasic image={eventLogoEventHub} useIntrinsicHeight />
            )}
        </div>

        <div className="container">
          <div className={`event-header__event-details ${eventHasFinished ? 'finished-event' : ''}`}>
            <div className="event-header__info">
              {eventTitle && (
                <h3 className="event-header__title">{eventTitle}</h3>
              )}
              {eventDates && (
                <div className="event-header__date">{eventDates}</div>
              )}
            </div>

            {(eventStartDate && !eventHasFinished) && (
              <div className="event-header__day">
                <span className="event-header__day__label">{translations.event.dayLabel}</span>
                <span className="event-header__day__count">
                  {isSingleDigit && '0'}
                  {eventDayCount}
                </span>
              </div>
            )}

            {(eventLocation && !isHomepage && !eventHasFinished) && (
              <div className="event-header__start-time">
                <span className="event-header__start-time__location">
                  {eventLocation}
                </span>

                <span className="event-header__start-time__time">
                  {time}
                </span>
              </div>
            )}

            {(isHomepage && organisedEvents && withLiveEvents && !eventHasFinished) && (
              <LiveEventsCarousel
                events={organisedEvents.liveEvents}
                liveNowLabel={translations.event.liveNowLabel}
              />
            )}
          </div>

          {(!isHomepage && organisedEvents && !!raceItems?.length && !eventHasFinished) && (
            <HubEventsCarousel
              pastEvents={organisedEvents.pastEvents}
              liveEvents={organisedEvents.liveEvents}
              upcomingEvents={organisedEvents.upcomingEvents}
              timezone={eventTime}
              carouselTitle={translations.event.todaysEventsLabel}
              liveOrUpcomingEventsCount={liveOrUpcomingEvents}
            />
          )}

          {((linkOne || linkTwo) && isHomepage) && (
            <div className={`event-header__links ${isOnlyOneLink ? 'single-link' : ''}`}>
              {linkOne && (
                <span className="event-header__link">
                  <Link
                    {...linkOne}
                    iconAfter={{
                      title: 'arrow-right',
                      reference: '#icon-think-arrow',
                    }}
                  />
                </span>
              )}

              {linkTwo && (
                <span className="event-header__link">
                  <Link
                    {...linkTwo}
                    iconAfter={{
                      title: 'arrow-right',
                      reference: '#icon-think-arrow',
                    }}
                  />
                </span>
              )}
            </div>
          )}
        </div>

        {(isHomepage && homepageCarousel) && (
          <div className={`event-header__header-carousel ${withLiveEvents ? '' : 'no-live-events-carousel'}`}>
            <HomepageHeader {...homepageCarousel} />
          </div>
        )}
      </div>

      {(displayLivePlaylist && !eventHasFinished) && (
        <Watch
          title={translations.event.watchPlaylistTitle}
          raceMode={false}
          eventHub
          playlist={liveStreamPlaylist}
        />
      )}
    </div>
  );
};

export default EventHeader;
